<template>
  <div>
    <!-- Loader -->
    <loading-panel :loading="loading"></loading-panel>

    <!-- Breadcrumbs Section (outside template-builder) -->
    <div class="breadcrumb-section">
      <breadcrumb style="margin-left: -15px">
        <breadcrumb-item>
          <a href="/mail/custom/landing">Email templates</a>
        </breadcrumb-item>
        <breadcrumb-item>
          <span>Email template types</span>
        </breadcrumb-item>
      </breadcrumb>
    </div>

    <div class="row">
      <div class="col">
        <card>
          <div>
            <div class="row align-items-center">
              <div class="col">
                <h4 slot="header" class="card-title">Email template types</h4>
              </div>
              <div class="col-auto d-none d-sm-block">
                <base-button size="sm" v-on:click="addTemplateType()" type="primary">
                  <i class="tim-icons icon-simple-add mr-2"></i> Create template type
                </base-button>
              </div>
              <div class="col-auto d-block d-sm-none">
                <base-button size="sm" v-on:click="addTemplateType()" type="primary">
                  <i class="tim-icons icon-simple-add"></i>
                </base-button>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="templateTypes.length <= 0 && !loading" class="text-center">
            <div class="font-icon-detail">
              <span style="font-size: 3em; color: #e3e3e3">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <h4 class="mt-3">No template types yet.</h4>
            </div>
          </div>

          <!-- Table View -->
          <div v-if="templateTypes.length > 0" class="mt-4">
            <!-- Desktop Table -->
            <div class="d-none d-sm-block">
              <b-table
                striped
                hover
                :items="filteredTypes"
                :fields="tableFields"
              >
                <!-- Type Info Column -->
                <template v-slot:cell(type_info)="row">
                  <div class="d-flex align-items-center">
                    <div class="type-icon mr-3" :style="{ backgroundColor: row.item.color + '20', color: row.item.color }">
                      <i :class="getIconClass(row.item.icon)"></i>
                    </div>
                    <div>
                      <div class="text-white">{{ row.item.name }}</div>
                      <div class="text-white-50 small">{{ row.item.description || 'No description provided' }}</div>
                    </div>
                  </div>
                </template>

                <!-- Type Column -->
                <template v-slot:cell(type)="row">
                  <span class="badge badge-sm" :class="row.item.is_system_default ? 'badge-info' : 'badge-default'">
                    {{ row.item.is_system_default ? 'System' : 'Custom' }}
                  </span>
                </template>

                <!-- Status Column -->
                <template v-slot:cell(status)="row">
                  <span :class="['badge', 'badge-' + (row.item.is_active ? 'success' : 'danger')]">
                    {{ row.item.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </template>

                <!-- Usage Column -->
                <template v-slot:cell(usage)="row">
                  <div class="text-white">
                    <i class="tim-icons icon-email-85 mr-1"></i>
                    {{ row.item.template_count || 0 }} template{{ (row.item.template_count || 0) !== 1 ? 's' : '' }}
                  </div>
                </template>

                <!-- Last Modified Column -->
                <template v-slot:cell(last_modified)="row">
                  <div class="text-white" v-if="row.item.last_modified">
                    {{ getRelativeTime(row.item.last_modified) }}
                  </div>
                  <div class="text-white" v-else>
                    Never updated
                  </div>
                </template>

                <!-- Actions Column -->
                <template v-slot:cell(actions)="row">
                  <el-dropdown>
                    <base-button simple type="primary" link size="sm">
                      <i class="tim-icons el-icon-more"></i>
                    </base-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <a class="dropdown-item mt-2 mb-2" @click.prevent="editTemplateType(row.item)" href="#">
                          <i class="tim-icons icon-pencil text-warning mr-2"></i> Edit template type
                        </a>
                        <hr>
                        <a class="dropdown-item mt-2 mb-2"
                           @click.prevent="deleteTemplateTypePrompt(row.item)" href="#">
                          <i class="tim-icons icon-trash-simple text-danger mr-2"></i> Archive template type
                        </a>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
<!--                  <span v-else>-->
<!--                    - -->
<!--                  </span>-->
                </template>
              </b-table>
            </div>

            <!-- Mobile View -->
            <div class="d-block d-sm-none">
              <b-table
                striped
                hover
                :items="filteredTypes"
                :fields="['template_types']"
              >
                <template v-slot:head(template_types)>
                  <span>Template Types: {{ filteredTypes.length }}</span>
                </template>

                <template v-slot:cell(template_types)="row">
                  <div class="mobile-template-type-card">
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="d-flex align-items-start">
                        <div class="type-icon mr-3" :style="{ backgroundColor: row.item.color + '20', color: row.item.color }">
                          <i :class="getIconClass(row.item.icon)"></i>
                        </div>
                        <div>
                          <p class="text-white">{{ row.item.name }}</p>
                          <p class="text-white small mb-2">{{ row.item.description || 'No description provided' }}</p>
                          <div class="d-flex justify-content-between align-items-center">
                            <small class="text-white">
                              <i class="tim-icons icon-email-85 mr-1"></i>
                              {{ row.item.template_count || 0 }} template{{ (row.item.template_count || 0) !== 1 ? 's' : '' }}
                            </small>
                            <small class="text-muted" v-if="row.item.updated">
                              {{ getRelativeTime(row.item.updated) }}
                            </small>
                          </div>
                        </div>
                      </div>
                      <el-dropdown v-if="!row.item.is_system_default && hasPermissions('template_update')">
                        <base-button simple type="primary" link size="sm">
                          <i class="tim-icons el-icon-more"></i>
                        </base-button>
                        <template #dropdown>
                          <el-dropdown-menu>
                            <a class="dropdown-item" @click.prevent="editTemplateType(row.item)">
                              <i class="tim-icons icon-pencil text-warning mr-2"></i> Edit
                            </a>
                            <a class="dropdown-item"
                               @click.prevent="deleteTemplateTypePrompt(row.item)">
                              <i class="tim-icons icon-trash-simple text-danger mr-2"></i> Archive
                            </a>
                          </el-dropdown-menu>
                        </template>
                      </el-dropdown>
                    </div>
                  </div>
                </template>
              </b-table>
            </div>
          </div>

          <!-- Pagination -->
          <b-pagination
            v-if="totalRows > perPage"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @input="getTemplateTypes(perPage, getOffset())"
            class="justify-content-center"
          ></b-pagination>
        </card>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <modal :show.sync="modals.create_template_type"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4 v-if="templateType.hasOwnProperty('id')" class="card-title">Edit template type</h4>
        <h4 v-else class="card-title">Create template type</h4>

        <!-- Name and Description -->
        <div class="row">
          <div class="col">
            <base-input required label="Name" :error="errs.name" v-model="templateType.name" placeholder="e.g., Customer onboarding">
            </base-input>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col">
            <base-input label="Description">
              <el-input
                v-model="templateType.description"
                :rows="3"
                type="textarea"
                placeholder="Brief description of when to use this template type."
                maxlength="500"
                show-word-limit
              />
            </base-input>
          </div>
        </div>

        <!-- Visual Customization -->
        <div class="row mt-3">
          <div class="col-lg-6">
            <label>Color theme</label>
            <div class="color-picker-container">
              <div class="color-options d-flex flex-wrap">
                <div
                  v-for="color in colorOptions"
                  :key="color"
                  class="color-option"
                  :class="{ active: templateType.color === color }"
                  :style="{ backgroundColor: color }"
                  @click="templateType.color = color">
                </div>
              </div>
              <div class="mt-2">
                <base-input label="...or choose custom color">
                  <el-color-picker
                    :predefine="colorOptions"
                    v-model="templateType.color"
                    size="small"
                  />
                </base-input>
              </div>
            </div>

          </div>
          <div class="col-lg-6">
            <label>Icon</label>
            <div class="icon-picker-container">
              <div class="icon-options d-flex flex-wrap">
                <div
                  v-for="icon in iconOptions"
                  :key="icon.value"
                  class="icon-option"
                  :class="{ active: templateType.icon === icon.value }"
                  @click="templateType.icon = icon.value"
                  :title="icon.label">
                  <i :class="getIconClass(icon.value)"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="row mt-4">
          <div class="col text-center">
            <base-button type="secondary" @click="modals.create_template_type = false">Cancel</base-button>
            <base-button v-if="templateType.hasOwnProperty('id')" class="ml-3" type="primary" @click="updateTemplateType(templateType)">
              Save changes
            </base-button>
            <base-button v-else class="ml-3" type="primary" @click="createTemplateType()">
              Create type
            </base-button>
          </div>
        </div>
      </card>
    </modal>
  </div>
</template>

<script>
import { BPagination, BTable } from "bootstrap-vue";
import {Dropdown, DropdownItem, DropdownMenu, ColorPicker} from "element-ui";
import { API_LOCATION } from "@/api-config";
import axios from "axios";
import swal from "sweetalert2";
import { Auth } from "aws-amplify";
import moment from "moment";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";

export default {
  name: "EmailTemplateTypes",
  components: {
    BPagination,
    BTable,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [ColorPicker.name]: ColorPicker,
    BreadcrumbItem,
    Breadcrumb
  },
  data() {
    return {
      loading: false,
      templateTypes: [],
      filteredTypes: [],
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      currentPage: 1,
      perPage: 12,
      totalRows: 0,
      typeFilter: 'all',

      templateType: {
        name: '',
        description: '',
        color: '#2563EB',
        icon: 'envelope',
        is_active: true
      },

      errs: {
        name: ''
      },

      modals: {
        create_template_type: false
      },

      // Table fields configuration
      tableFields: [
        { key: 'type_info', label: 'Template Type' },
        { key: 'usage', label: 'Usage' },
        { key: 'last_modified', label: 'Last Modified' },
        { key: 'actions', label: 'Actions' }
      ],

      colorOptions: ['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4'],

      iconOptions: [
        { value: 'compass-05', label: 'Envelope' },
        { value: 'email-85', label: 'Email' },
        { value: 'palette', label: 'Notification' },
        { value: 'bell-55', label: 'Bell' },
        { value: 'chat-33', label: 'Chat' },
        { value: 'send', label: 'Send' },
        { value: 'paper', label: 'Certificate' },
        { value: 'gift-2', label: 'Gift' },
        { value: 'heart-2', label: 'Heart' },
        { value: 'shape-star', label: 'Star' },
        { value: 'tv-2', label: 'Award' },
        { value: 'trophy', label: 'Trophy' },
        { value: 'tag', label: 'Tag' },
        { value: 'bank', label: 'Folder' },
        { value: 'badge', label: 'Business' },
        { value: 'calendar-60', label: 'Calendar' }
      ]
    }
  },
  methods: {
    hasPermissions(scope) {
      let permissions = JSON.parse(localStorage.getItem('permissions'));
      return permissions.some(element => element === scope);
    },

    getIconClass(iconName) {
      return `tim-icons icon-${iconName}`;
    },

    getRelativeTime(dateString) {
      const now = moment();
      const inputDate = moment(dateString);
      const diffInDays = now.diff(inputDate, 'days');
      const diffInWeeks = now.diff(inputDate, 'weeks');
      const diffInMonths = now.diff(inputDate, 'months');

      if (diffInDays < 1) return 'Today';
      if (diffInDays === 1) return 'Yesterday';
      if (diffInDays < 7) return `${diffInDays} days ago`;
      if (diffInWeeks === 1) return 'Last week';
      if (diffInWeeks < 4) return `${diffInWeeks} weeks ago`;
      if (diffInMonths === 1) return 'Last month';
      return `${diffInMonths} months ago`;
    },

    filterTypes() {
      if (this.typeFilter === 'all') {
        this.filteredTypes = this.templateTypes;
      } else if (this.typeFilter === 'system') {
        this.filteredTypes = this.templateTypes.filter(type => type.is_system_default);
      } else if (this.typeFilter === 'custom') {
        this.filteredTypes = this.templateTypes.filter(type => !type.is_system_default);
      }
    },

    getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken);
          })
          .catch(err => console.log(err));
      });
    },

    async addTemplateType() {
      this.templateType = {
        name: '',
        description: '',
        color: '#2563EB',
        icon: 'envelope',
        is_active: true
      };
      this.errs.name = '';
      this.modals.create_template_type = true;
    },

    editTemplateType(item) {
      this.templateType = { ...item };
      this.errs.name = '';
      this.modals.create_template_type = true;
    },

    validateFields() {
      let errors = 0;

      if (this.templateType.name.trim() === '') {
        this.errs.name = 'Template type name cannot be empty';
        errors++;
      } else if (this.templateType.name.length > 100) {
        this.errs.name = 'Template type name cannot exceed 100 characters';
        errors++;
      } else {
        this.errs.name = '';
      }

      return errors === 0;
    },

    async createTemplateType() {
      if (!this.validateFields()) {
        return;
      }

      this.loading = true;
      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };
      let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-template-types`;

      let data = {
        ...this.templateType,
        name: this.templateType.name.trim()
      };

      axios.post(url, data, config)
        .then(response => {
          this.loading = false;
          this.modals.create_template_type = false;
          swal('Success!', 'Template type created successfully', 'success');
          this.getTemplateTypes(this.perPage, this.getOffset());
        })
        .catch(err => {
          this.loading = false;
          if (err.response?.status === 401) {
            this.$router.push('/forbidden');
          } else if (err.response?.status === 422) {
            swal('Error!', 'A template type with this name already exists', 'error');
          } else {
            swal('Error!', 'Something went wrong', 'error');
          }
        });
    },

    async updateTemplateType(item) {
      if (!this.validateFields()) {
        return;
      }

      this.loading = true;
      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };
      let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-template-types/${item.id}`;

      let data = {
        ...this.templateType,
        name: this.templateType.name.trim()
      };

      axios.put(url, data, config)
        .then(response => {
          this.loading = false;
          this.modals.create_template_type = false;
          swal('Success!', 'Template type updated successfully', 'success');
          this.getTemplateTypes(this.perPage, this.getOffset());
        })
        .catch(err => {
          this.loading = false;
          if (err.response?.status === 401) {
            this.$router.push('/forbidden');
          } else {
            swal('Error!', 'Something went wrong', 'error');
          }
        });
    },

    deleteTemplateTypePrompt(item) {
      swal({
        title: 'Are you sure?',
        text: `This will archive the "${item.name}" template type. This action cannot be undone.`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!'
      }).then((response) => {
        if (response.value === true) {
          this.deleteTemplateType(item);
        }
      });
    },

    async deleteTemplateType(item) {
      this.loading = true;
      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };
      let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-template-types/${item.id}`;

      axios.delete(url, config)
        .then(response => {
          this.loading = false;
          swal('Deleted!', 'Template type has been deleted', 'success');
          this.getTemplateTypes(this.perPage, this.getOffset());
        })
        .catch(err => {
          this.loading = false;
          if (err.response?.status === 401) {
            this.$router.push('/forbidden');
          } else if (err.response?.status === 422) {
            swal('Error!', 'Cannot delete template type that is being used by templates', 'error');
          } else {
            swal('Error!', 'Something went wrong', 'error');
          }
        });
    },

    async getTemplateTypes(count, offset, shouldInit = false) {
      this.loading = true;
      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };
      let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-template-types?count=${count}&offset=${offset}`;

      if (shouldInit) {
        url += '&init=true';
      }

      axios.get(url, config)
        .then(response => {
          this.loading = false;
          this.templateTypes = response.data.template_types || [];
          this.totalRows = response.data.total_rows || 0;
          this.filterTypes();
        })
        .catch(err => {
          this.loading = false;
          if (err.response?.status === 401) {
            this.$router.push('/forbidden');
          } else {
            swal('Error!', 'Something went wrong', 'error');
          }
        });
    },

    getOffset() {
      return Number((this.currentPage * this.perPage) - this.perPage);
    }
  },

  mounted() {
    this.perPage = this.$route.query.count ? Number(this.$route.query.count) : 10;
    this.currentPage = this.$route.query.page ? Number(this.$route.query.page) : 1;

    // Check for init parameter and remove it from URL after use
    const shouldInit = this.$route.query.init === '1';

    if (shouldInit) {
      // Remove init parameter from URL
      const newQuery = { ...this.$route.query };
      delete newQuery.init;

      this.$router.replace({
        path: this.$route.path,
        query: newQuery
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    }

    // Call API with init parameter if it was present
    this.getTemplateTypes(this.perPage, this.getOffset(), shouldInit);
  }
};
</script>

<style scoped>
</style>
